import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Users, Building2, Globe2, Check, Lock, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Production House governance card.
 * - Approval rule (single / any_partner / all_partners) for critical project actions
 * - Email-domain auto-discovery toggle (separate from invites — used for staff
 *   sign-up suggestions)
 */
export default function ProductionGovernanceCard({ org }) {
  const [approval, setApproval] = useState(null);
  const [savingRule, setSavingRule] = useState(false);
  const [domain, setDomain]   = useState(org?.email_domain || "");
  const [domainOn, setDomainOn] = useState(!!org?.email_domain_join_enabled);
  const [savingDomain, setSavingDomain] = useState(false);

  useEffect(() => {
    if (!org?.id) return;
    api.get(`/orgs/${org.id}/partner-approval`)
       .then(({ data }) => setApproval(data))
       .catch(() => {});
  }, [org?.id]);

  const setRule = async (rule) => {
    setSavingRule(true);
    try {
      const { data } = await api.patch(`/orgs/${org.id}/partner-approval`, { rule });
      setApproval(data);
      toast.success(`Rule set to ${rule.replace("_", " ")}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
    } finally {
      setSavingRule(false);
    }
  };

  const saveDomain = async () => {
    setSavingDomain(true);
    try {
      const { data } = await api.patch(`/orgs/${org.id}/domain-join`, {
        enabled: domainOn,
        domain:  domain.trim() || undefined,
      });
      setDomain(data.domain || "");
      setDomainOn(data.enabled);
      toast.success(data.enabled ? `Discovery enabled for @${data.domain}` : "Discovery off");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSavingDomain(false);
    }
  };

  if (!org || org.type !== "production_house") return null;

  return (
    <section className="cs-card rounded-2xl p-6 mb-8" data-testid="ph-governance-card">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/55 flex items-center gap-2">
            <Building2 size={11} /> Production House · Governance
          </div>
          <h2 className="font-display text-2xl uppercase tracking-tight mt-1">
            Partner Approval Rule
          </h2>
        </div>
      </header>

      <p className="mt-3 text-sm text-white/55 max-w-2xl">
        How critical project actions (crew finalize, project wrap, large adds) get
        signed off. Separate from Trade Org committee voting.
      </p>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="approval-rule-grid">
        {[
          { v: "single",       title: "Single Producer",
            sub: "Solo sign-off. You act alone." },
          { v: "any_partner",  title: "Any Partner",
            sub: "Any registered partner can act on behalf of the house." },
          { v: "all_partners", title: "All Partners",
            sub: "Co-signature required from every partner before action commits." },
        ].map((opt) => {
          const selected = approval?.rule === opt.v;
          return (
            <button key={opt.v}
                    disabled={savingRule}
                    onClick={() => setRule(opt.v)}
                    data-testid={`approval-rule-${opt.v}`}
                    className={`text-left rounded-xl px-4 py-4 border transition-all
                      ${selected
                        ? "border-[#F5B82E]/60 bg-[#F5B82E]/10"
                        : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-display uppercase tracking-tight">{opt.title}</div>
                {selected && <Check size={14} className="text-[#F5B82E]" />}
              </div>
              <div className="mt-1.5 text-[11px] leading-relaxed text-white/55">{opt.sub}</div>
            </button>
          );
        })}
      </div>

      {/* Partners */}
      <div className="mt-6">
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 flex items-center gap-2">
          <Users size={11} /> Registered Partners
        </div>
        <ul className="mt-3 flex flex-wrap gap-2" data-testid="partner-list">
          {(approval?.partners || []).map((p) => (
            <li key={p.id}
                data-testid={`partner-${p.id}`}
                className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[11px]">
              <span>{p.name}</span>
              <span className="ml-2 text-[9px] tracking-[0.22em] uppercase text-white/45">
                {p.is_owner ? "owner" : (p.organization_role || "")}
              </span>
            </li>
          ))}
          {(approval?.partners || []).length === 0 && (
            <li className="text-sm text-white/45">No registered partners yet.</li>
          )}
        </ul>
        {approval?.rule === "all_partners" && (approval?.partners?.length || 0) < 2 && (
          <div className="mt-3 flex items-center gap-2 text-[11px] text-amber-200">
            <AlertTriangle size={13} />
            All-partners mode needs at least 2 partners. Invite a Producer / Line Producer from Team.
          </div>
        )}
      </div>

      {/* Domain join */}
      <div className="mt-7 pt-6 border-t border-white/10">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55 flex items-center gap-2">
              <Globe2 size={11} /> Email Domain Discovery
            </div>
            <p className="text-sm text-white/55 mt-1 max-w-xl">
              When ON, anyone signing up with <span className="text-white">@your-domain.com</span> sees
              a banner suggesting your house — speeds up team onboarding alongside WhatsApp invites.
            </p>
          </div>
          <Switch checked={domainOn} onCheckedChange={setDomainOn}
                  data-testid="domain-discovery-toggle" />
        </div>

        {domainOn && (
          <div className="mt-4 flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-[10px] tracking-[0.25em] uppercase text-white/55">Company domain</Label>
              <Input value={domain} onChange={(e) => setDomain(e.target.value.toLowerCase())}
                     placeholder="mehrastudios.com"
                     className="mt-1.5 bg-black/30 border-white/15 font-mono"
                     data-testid="domain-input" />
            </div>
            <Button onClick={saveDomain} disabled={savingDomain}
                    className="h-10 px-5 rounded-full text-[10px] uppercase tracking-[0.22em] btn-themed"
                    data-testid="domain-save">
              {savingDomain ? "Saving…" : "Save"}
            </Button>
          </div>
        )}
        {!domainOn && domain && (
          <div className="mt-3 text-[11px] text-white/45 flex items-center gap-1.5">
            <Lock size={12} /> Previously @{domain} — toggle on to re-enable discovery.
          </div>
        )}
      </div>
    </section>
  );
}
